import React, { useState, useEffect, useCallback } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput, Image, Animated } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useApi } from "../context/ApiContext";
import { useProject } from "../context/ProjectContext";
import { useApp } from "../context/AppContext";
import { DataProvider, useData } from "../context/DataContext";
import PublishModal from "../components/PublishModal";
import ServicesContent from "../components/ServicesContent";
import ProductsContent from "../components/ProductsContent";
import StatCard from "../components/StatCard";
import { useAuth } from "../context/AuthContext";
import SharedHeader from "../components/SharedHeader";
import NavButton from "../components/NavButton";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area } from "recharts";
// Mock data for the charts
const mockData = {
	currentVersion: "0.0.1",
	lastPublished: "2 days ago",
	activeUsers: "12.5k",
	dailyNewUsers: "+1.2k",
	revenue: "$15,240",
	revenueChange: "+12.5%",
};

// Fun tech loading messages
const loadingMessages = [
	"Compiling your digital thoughts...",
	"Debugging the matrix...",
	"Downloading more ram...",
	"Forcasting a cloudy day (sponsored by AWS)...",
	"Aligning your bits and bytes...",
	"Loading device drivers...",
	"Threading CPU processes...",
	"Initializing neural networks...",
	"Defragmenting the interwebs...",
];

/**
 * Returns a random loading message from the loadingMessages array
 */
const getRandomLoadingMessage = () => {
	const randomIndex = Math.floor(Math.random() * loadingMessages.length);
	return loadingMessages[randomIndex];
};

/**
 * Returns a human-readable time difference string (e.g., "2 hours ago", "3 days ago")
 */
const timeAgo = (date) => {
	const now = new Date();
	const diffInMs = now - new Date(date);
	const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
	const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
	const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
	const diffInWeeks = Math.floor(diffInDays / 7);
	const diffInMonths = Math.floor(diffInDays / 30);
	const diffInYears = Math.floor(diffInDays / 365);

	switch (true) {
		case diffInMinutes < 1:
			return "Just now";
		case diffInMinutes < 60:
			return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
		case diffInHours < 24:
			return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
		case diffInDays < 7:
			return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
		case diffInWeeks < 4:
			return `${diffInWeeks} week${diffInWeeks === 1 ? "" : "s"} ago`;
		case diffInMonths < 12:
			return `${diffInMonths} month${diffInMonths === 1 ? "" : "s"} ago`;
		default:
			return `${diffInYears} year${diffInYears === 1 ? "" : "s"} ago`;
	}
};

const AppContent = ({ appId, navigate, hasActiveAppleDeveloper, onPublish, onGoToServices }) => {
	const [isHovered, setIsHovered] = React.useState(false);
	const [isBuildInfoHovered, setIsBuildInfoHovered] = React.useState(false);
	const slideAnim = React.useRef(new Animated.Value(0)).current;
	const pulseAnim = React.useRef(new Animated.Value(1)).current;
	const AnimatedTouchableOpacity = Animated.createAnimatedComponent(TouchableOpacity);
	const { pages, activePage } = useApp();
	const { appData, telemetryData } = useData();
	const [activityData, setActivityData] = useState([]);

	// Get build status from appData
	const buildStatus = appData?.buildStatus || null;

	// Setup pulsing animation for queue state
	useEffect(() => {
		if (buildStatus === "queue") {
			const pulse = () => {
				Animated.sequence([
					Animated.timing(pulseAnim, {
						toValue: 0.7,
						duration: 1000,
						useNativeDriver: false,
					}),
					Animated.timing(pulseAnim, {
						toValue: 1,
						duration: 1000,
						useNativeDriver: false,
					}),
				]).start(() => pulse());
			};
			pulse();
		} else {
			pulseAnim.setValue(1);
		}
	}, [buildStatus, pulseAnim]);

	/**
	 * Returns button configuration based on build status
	 */
	const getButtonConfig = () => {
		switch (buildStatus) {
			case "BUILDING":
				return {
					icon: "hammer",
					text: "Building",
					color: "#FF9500",
					textColor: "#fff",
					disabled: true,
				};
			case "FAILED":
				return {
					icon: "close-circle",
					text: "Retry Build",
					color: "#FF3B30",
					textColor: "#fff",
					disabled: false,
				};
			case "queue":
				return {
					icon: "time",
					text: "Build in Queue...",
					color: "#0A84FF",
					textColor: "#fff",
					disabled: true,
				};
			case "COMPLETED":
				return {
					icon: "airplane",
					text: "Upload to Testflight",
					color: "#0A84FF",
					textColor: "#fff",
					disabled: false,
				};
			default:
				return {
					icon: "airplane",
					text: "Upload to Testflight",
					color: hasActiveAppleDeveloper ? "#0A84FF" : "#333",
					textColor: hasActiveAppleDeveloper ? "#fff" : "#666",
					disabled: !hasActiveAppleDeveloper,
				};
		}
	};

	/**
	 * Returns slideout content based on build status
	 */
	const getSlideoutContent = () => {
		switch (buildStatus) {
			case "BUILDING":
				return {
					title: "",
					subtitle: "This may take a few minutes...",
				};
			case "FAILED":
				return {
					title: "",
					subtitle: "Retry?",
				};
			case "queue":
				return {
					title: "",
					subtitle: "Your build is waiting to start...",
				};
			case "COMPLETED":
				return {
					title: appData.buildVersion ? `v${appData.buildVersion}(${appData.buildNumber}) Complete` : "Build Complete",
					subtitle: appData.buildDate ? timeAgo(appData.buildDate) : "Ready for upload",
				};
			default:
				return {
					title: "No Current Build Available",
					subtitle: "No Build Date Available",
				};
		}
	};

	const buttonConfig = getButtonConfig();
	const slideoutContent = getSlideoutContent();

	const slideIn = () => {
		setIsBuildInfoHovered(true);
		Animated.timing(slideAnim, {
			toValue: 1,
			duration: 200,
			useNativeDriver: false,
		}).start();
	};

	const slideOut = () => {
		Animated.timing(slideAnim, {
			toValue: 0,
			duration: 200,
			useNativeDriver: false,
		}).start(() => {
			setIsBuildInfoHovered(false);
		});
	};

	/**
	 * Handles button press based on build status
	 */
	const handleButtonPress = () => {
		// Hide the slideout when button is clicked
		slideOut();

		switch (buildStatus) {
			case "FAILED":
				// Retry build
				onPublish();
				break;
			case "BUILDING":
			case "queue":
				// Do nothing for these states
				break;
			default:
				if (hasActiveAppleDeveloper) {
					onPublish();
				} else {
					onGoToServices();
				}
				break;
		}
	};

	// Process telemetry data to format for chart
	useEffect(() => {
		if (telemetryData && telemetryData.length > 0) {
			// Get current date info
			console.log("telemetryData", telemetryData);
			const currentDate = new Date();
			const currentYear = currentDate.getFullYear();
			const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

			// Calculate previous month
			let prevMonth = currentMonth - 1;
			let prevYear = currentYear;
			if (prevMonth === 0) {
				prevMonth = 12;
				prevYear -= 1;
			}

			// Format month strings
			const currentMonthStr = `${currentYear}-${currentMonth.toString().padStart(2, "0")}`;
			const prevMonthStr = `${prevYear}-${prevMonth.toString().padStart(2, "0")}`;

			// Find data for both months
			const currentMonthData = telemetryData.find((data) => data.monthRange === currentMonthStr);
			const prevMonthData = telemetryData.find((data) => data.monthRange === prevMonthStr);

			// Find earliest date with data
			let earliestDate = null;
			let earliestDateWithData = null;

			// Check current month data
			if (currentMonthData && currentMonthData.ccuPeak) {
				Object.keys(currentMonthData.ccuPeak).forEach((dayStr) => {
					if (currentMonthData.ccuPeak[dayStr] !== null) {
						const date = new Date(currentYear, currentMonth - 1, parseInt(dayStr));
						if (!earliestDateWithData || date < earliestDateWithData) {
							earliestDateWithData = date;
						}
					}
				});
			}

			// Check previous month data
			if (prevMonthData && prevMonthData.ccuPeak) {
				Object.keys(prevMonthData.ccuPeak).forEach((dayStr) => {
					if (prevMonthData.ccuPeak[dayStr] !== null) {
						const date = new Date(prevYear, prevMonth - 1, parseInt(dayStr));
						if (!earliestDateWithData || date < earliestDateWithData) {
							earliestDateWithData = date;
						}
					}
				});
			}

			// Set start date to one day before earliest data point, or default to 60 days ago
			let startDate;
			if (earliestDateWithData) {
				startDate = new Date(earliestDateWithData);
				startDate.setDate(startDate.getDate() - 1);
			} else {
				startDate = new Date();
				startDate.setDate(startDate.getDate() - 60);
			}

			// Create chart data array
			const chartData = [];
			const endDate = new Date(); // Today

			// Loop through days from start date to today
			for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
				const year = date.getFullYear();
				const month = date.getMonth() + 1;
				const day = date.getDate();

				const monthStr = `${year}-${month.toString().padStart(2, "0")}`;
				const dayStr = day.toString();

				// Find data for this day
				let value = null;
				if (monthStr === currentMonthStr && currentMonthData && currentMonthData.ccuPeak && currentMonthData.ccuPeak[dayStr]) {
					value = currentMonthData.ccuPeak[dayStr];
				} else if (monthStr === prevMonthStr && prevMonthData && prevMonthData.ccuPeak && prevMonthData.ccuPeak[dayStr]) {
					value = prevMonthData.ccuPeak[dayStr];
				}

				// Determine if this is today's date
				const today = new Date();
				const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();

				// Set missing data to 0 for all past dates (but not today)
				if (value === null && !isToday) {
					value = 0;
				}

				// Add point regardless of data availability
				chartData.push({
					date: new Date(date),
					day: day,
					month: month,
					formattedDate: `${month}/${day}`,
					users: value,
				});
			}

			setActivityData(chartData);
		}
	}, [telemetryData]);

	return (
		<>
			<View style={styles.previewSection}>
				<View style={styles.phonePreview}>
					<View style={styles.phoneFrame}>
						<View style={styles.phoneContent} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
							{isHovered ? (
								<TouchableOpacity style={styles.canvasButton} onPress={() => navigate(`/canvas/${appId}`)}>
									<Text style={styles.canvasButtonText}>Go to Canvas</Text>
									<Ionicons name="arrow-forward" size={20} color="#fff" />
								</TouchableOpacity>
							) : pages && pages.length > 0 ? (
								<ScrollView style={styles.pagesPreview}>
									{pages.map((page) => (
										<View key={page.id} style={[styles.pagePreviewItem, page.id === activePage && styles.activePagePreviewItem]}>
											<Ionicons name={page.icon} size={16} color={page.id === activePage ? "#fff" : "#666"} />
											<Text style={[styles.pagePreviewText, page.id === activePage && styles.activePagePreviewText]}>{page.title}</Text>
										</View>
									))}
								</ScrollView>
							) : (
								<Text style={styles.previewPlaceholder}>No pages yet</Text>
							)}
						</View>
					</View>
				</View>
			</View>

			<ScrollView style={styles.infoSection}>
				<View style={styles.infoHeader}>
					<Text style={styles.infoTitle}>Publishing</Text>
					<View style={styles.buildInfoContainer} onMouseEnter={slideIn} onMouseLeave={slideOut}>
						<AnimatedTouchableOpacity
							style={[
								styles.testflightButton,
								{
									backgroundColor: buttonConfig.color,
									opacity: buildStatus === "queue" ? pulseAnim : 1,
									borderBottomLeftRadius: slideAnim.interpolate({
										inputRange: [0, 1],
										outputRange: [8, 0],
									}),
									borderBottomRightRadius: slideAnim.interpolate({
										inputRange: [0, 1],
										outputRange: [8, 0],
									}),
								},
							]}
							disabled={buttonConfig.disabled}
							onPress={handleButtonPress}>
							<Ionicons name={buttonConfig.icon} size={20} color={buttonConfig.textColor} />
							<Text style={[styles.testflightButtonText, { color: buttonConfig.textColor }]}>{buttonConfig.text}</Text>
						</AnimatedTouchableOpacity>

						{isBuildInfoHovered && (
							<Animated.View
								style={[
									styles.buildInfoSlideOut,
									{
										maxHeight: slideAnim.interpolate({
											inputRange: [0, 1],
											outputRange: [0, 80],
										}),
										opacity: slideAnim,
										borderColor: buttonConfig.color,
									},
								]}>
								<Text style={styles.buildInfoVersion}>{slideoutContent.title}</Text>
								<Text style={styles.buildInfoDate}>{slideoutContent.subtitle}</Text>
							</Animated.View>
						)}
					</View>
				</View>

				<View style={styles.statsGrid}>
					<StatCard title="Active Users" value={mockData.activeUsers} change={mockData.dailyNewUsers} icon="people-outline" />
					<StatCard title="Revenue" value={mockData.revenue} change={mockData.revenueChange} icon="cash-outline" />
				</View>

				<View style={styles.chartSection}>
					<Text style={styles.chartTitle}>Concurrent Users</Text>
					<Text style={styles.chartDescription}>Showing peak concurrency for the last 60 days</Text>
					{activityData.length > 0 ? (
						<View style={styles.chartContainer}>
							<ResponsiveContainer width="100%" height={200}>
								<LineChart
									data={activityData}
									margin={{
										top: 20,
										right: 30,
										left: 10,
										bottom: 10,
									}}
									style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
									<CartesianGrid strokeDasharray="3 3" stroke="#444" />
									<XAxis dataKey="formattedDate" tick={{ fill: "#999", fontSize: 12, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }} tickCount={7} scale="point" />
									<YAxis
										tick={{ fill: "#999", fontSize: 12, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}
										width={40}
										domain={[
											0,
											(dataMax) => {
												// Find the maximum value in the activityData
												const maxValue = Math.max(...activityData.filter((item) => item.users !== null).map((item) => item.users));
												// Add padding: 20% or 1.5, whichever is greater
												const additionalPadding = Math.max(maxValue * 0.2, 1.5);
												return Math.ceil(maxValue + additionalPadding);
											},
										]}
										tickFormatter={(value) => Math.floor(value)}
										allowDecimals={false}
									/>
									<Tooltip
										contentStyle={{
											backgroundColor: "#333",
											border: "none",
											borderRadius: 8,
											color: "#fff",
											fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
										}}
										labelStyle={{
											color: "#fff",
											fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
											fontWeight: 500,
										}}
										itemStyle={{
											color: "#0A84FF",
											fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
										}}
										formatter={(value) => [`${value} users`, "Peak CCU"]}
										labelFormatter={(value) => `Date: ${value}`}
									/>
									<Area type="monotone" dataKey="users" stroke="#0A84FF" fill="rgba(10, 132, 255, 0.1)" strokeWidth={2} activeDot={{ r: 6, fill: "#0A84FF", stroke: "#fff" }} connectNulls={true} />
									<Line type="monotone" dataKey="users" stroke="#0A84FF" strokeWidth={2} dot={false} activeDot={{ r: 6, fill: "#0A84FF", stroke: "#fff" }} connectNulls={true} />
								</LineChart>
							</ResponsiveContainer>
						</View>
					) : (
						<View style={styles.chartPlaceholder}>
							<Text style={styles.placeholderText}>No Activity Data Available</Text>
						</View>
					)}
				</View>

				<View style={styles.chartSection}>
					<Text style={styles.chartTitle}>Revenue</Text>
					<View style={styles.chartPlaceholder}>
						<Text style={styles.placeholderText}>Revenue Chart</Text>
					</View>
				</View>
			</ScrollView>
		</>
	);
};

const AppHome = () => {
	const { appId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const [activeSection, setActiveSection] = React.useState("app");
	const [error, setError] = React.useState(null);
	const { api } = useApi();
	const { setCurrentProject } = useProject();
	const [services, setServices] = React.useState([]);
	const [showPublishModal, setShowPublishModal] = React.useState(false);
	const { appData, loading, error: dataError } = useData();

	const { user, logout } = useAuth();
	const logoutFunction = () => {
		logout();
		navigate("/login");
	};

	useEffect(() => {
		if (appData._id) {
			//lets set the legacy states
			setCurrentProject(appData);
			setServices(appData.services);
		}
	}, [appData]);

	const hasActiveAppleDeveloper = services.some((service) => service.type === "apple_developer" && service.status === "active");

	const renderContent = () => {
		if (loading.app || loading.team || loading.canvas) {
			return (
				<View style={styles.loadingContainer}>
					<Text style={styles.loadingText}>{getRandomLoadingMessage()}</Text>
				</View>
			);
		}

		if (error) {
			return (
				<View style={styles.errorContainer}>
					<Text style={styles.errorText}>Error: {error}</Text>
				</View>
			);
		}

		switch (activeSection) {
			case "services":
				return <ServicesContent appId={appId} api={api} project={location.state?.project} />;
			case "products":
				return <ProductsContent appId={appId} />;
			default:
				return <AppContent appId={appId} navigate={navigate} hasActiveAppleDeveloper={hasActiveAppleDeveloper} onPublish={() => setShowPublishModal(true)} onGoToServices={() => setActiveSection("services")} />;
		}
	};

	return (
		<View style={styles.container}>
			<SharedHeader user={user} onLogout={logoutFunction} onNavigateToSettings={() => navigate("/settings")} notifications={[]} onDismissNotification={() => {}} onNavigateToProjects={() => navigate("/")} isProjectsActive={false}>
				<View style={styles.appHomeHeaderNavContent}>
					<Text style={styles.breadcrumbSeparator}>/</Text>
					<View style={styles.breadcrumbs}>
						<View style={styles.breadcrumbItem}>
							<Text style={styles.breadcrumbTextActive}>{location.state?.project?.name || "Loading..."}</Text>
						</View>
					</View>
					<View style={styles.navDivider} />
					<View style={styles.navigation}>
						<NavButton icon="apps-outline" label="App" isActive={activeSection === "app"} onClick={() => setActiveSection("app")} />
						<NavButton icon="cube-outline" label="Services" isActive={activeSection === "services"} onClick={() => setActiveSection("services")} />
						<NavButton icon="cube-outline" label="Products" isActive={activeSection === "products"} onClick={() => setActiveSection("products")} />
					</View>
				</View>
			</SharedHeader>

			<View style={styles.content}>{renderContent()}</View>

			<PublishModal isVisible={showPublishModal} onClose={() => setShowPublishModal(false)} appId={appId} pages={location.state?.project?.pages || []} services={services} />
		</View>
	);
};

// Wrapper component that provides DataContext to AppHome
const AppHomeWithData = () => {
	const { appId } = useParams();
	const location = useLocation();
	const initialProject = location.state?.project;
	console.log("AppHomeWithData: initialProject", appId, initialProject);
	return (
		<DataProvider key={appId} appId={appId} initialData={initialProject}>
			<AppHome />
		</DataProvider>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#1A1A1A",
	},
	appHomeHeaderNavContent: {
		flexDirection: "row",
		alignItems: "center",
		gap: 24,
	},
	breadcrumbs: {
		flexDirection: "row",
		alignItems: "center",
	},
	breadcrumbSeparator: {
		color: "#666",
		fontSize: 15,
		fontWeight: "300",
	},
	breadcrumbItem: {
		flexDirection: "row",
		alignItems: "center",
	},
	breadcrumbTextActive: {
		color: "#fff",
		fontSize: 15,
		fontWeight: "600",
	},
	navDivider: {
		width: 1,
		height: 28,
		backgroundColor: "#2A2A2A",
	},
	navigation: {
		flexDirection: "row",
		gap: 8,
	},
	content: {
		flex: 1,
		flexDirection: "row",
		padding: 20,
		gap: 20,
	},
	previewSection: {
		width: "40%",
		alignItems: "center",
	},
	phonePreview: {
		alignItems: "center",
		gap: 20,
	},
	phoneFrame: {
		width: 375,
		height: 812,
		backgroundColor: "#2A2A2A",
		borderRadius: 40,
		padding: 20,
		alignItems: "center",
		justifyContent: "center",
	},
	phoneContent: {
		flex: 1,
		width: "100%",
		backgroundColor: "#333",
		borderRadius: 20,
		alignItems: "center",
		justifyContent: "center",
		position: "relative",
	},
	previewPlaceholder: {
		color: "#666",
		fontSize: 16,
	},
	canvasButton: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#0A84FF",
		borderRadius: 8,
		paddingHorizontal: 20,
		height: 48,
		gap: 8,
	},
	canvasButtonText: {
		color: "#fff",
		fontSize: 16,
		fontWeight: "600",
	},
	infoSection: {
		flex: 1,
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		padding: 20,
	},
	infoHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 20,
	},
	infoTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
	},
	buildInfoContainer: {
		position: "relative",
		flexDirection: "column",
		alignItems: "flex-end",
	},
	testflightButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		borderRadius: 8,
		padding: 12,
		width: 195,
		paddingHorizontal: 16,
		justifyContent: "center",
	},
	statsGrid: {
		flexDirection: "row",
		gap: 16,
		marginBottom: 20,
	},
	chartSection: {
		marginBottom: 20,
	},
	chartTitle: {
		fontSize: 16,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 4,
	},
	chartDescription: {
		fontSize: 12,
		color: "#666",
		marginBottom: 12,
	},
	chartContainer: {
		height: 200,
		backgroundColor: "#333",
		borderRadius: 12,
		alignItems: "center",
		justifyContent: "center",
	},
	chartContainer: {
		height: 200,
		backgroundColor: "#333",
		borderRadius: 12,
		overflow: "hidden",
		padding: 0,
		boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
	},
	chartPlaceholder: {
		height: 200,
		backgroundColor: "#333",
		borderRadius: 12,
		alignItems: "center",
		justifyContent: "center",
	},
	placeholderText: {
		color: "#666",
		fontSize: 14,
	},
	fullContent: {
		flex: 1,
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		padding: 20,
	},
	contentHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 24,
	},
	contentTitle: {
		fontSize: 20,
		fontWeight: "600",
		color: "#fff",
	},
	addButton: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#0A84FF",
		borderRadius: 8,
		paddingHorizontal: 16,
		height: 40,
		gap: 8,
	},
	addButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
	productsGrid: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 16,
		marginTop: 24,
	},
	productCard: {
		width: "calc(33.33% - 11px)",
		aspectRatio: 1,
		backgroundColor: "#333",
		borderRadius: 12,
		padding: 16,
		alignItems: "center",
		justifyContent: "center",
	},
	settingsSection: {
		marginBottom: 32,
	},
	sectionTitle: {
		fontSize: 16,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 16,
	},
	settingsGroup: {
		backgroundColor: "#333",
		borderRadius: 12,
		padding: 16,
	},
	settingItem: {
		marginBottom: 16,
	},
	settingLabel: {
		fontSize: 14,
		color: "#666",
		marginBottom: 8,
	},
	settingInput: {
		backgroundColor: "#2A2A2A",
		borderRadius: 8,
		padding: 12,
		color: "#fff",
		fontSize: 14,
	},
	teamList: {
		height: 200,
		alignItems: "center",
		justifyContent: "center",
	},
	tableSection: {
		marginTop: 24,
	},
	tableTitle: {
		fontSize: 16,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 12,
	},
	tablePlaceholder: {
		height: 300,
		backgroundColor: "#333",
		borderRadius: 12,
		alignItems: "center",
		justifyContent: "center",
	},
	loadingContainer: {
		flex: 1,
		justifyContent: "center",
		alignItems: "center",
	},
	loadingText: {
		color: "#666",
		fontSize: 16,
	},
	errorContainer: {
		flex: 1,
		justifyContent: "center",
		alignItems: "center",
	},
	errorText: {
		color: "#ff4444",
		fontSize: 16,
	},
	pagesPreview: {
		width: "100%",
		padding: 16,
	},
	pagePreviewItem: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#333",
		marginBottom: 8,
	},
	activePagePreviewItem: {
		backgroundColor: "#0A84FF",
	},
	pagePreviewText: {
		color: "#666",
		fontSize: 14,
	},
	activePagePreviewText: {
		color: "#fff",
		fontWeight: "600",
	},
	testflightButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
	buildInfoSlideOut: {
		position: "absolute",
		top: "100%",
		right: 0,
		left: 0,
		backgroundColor: "#333",
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
		borderWidth: 1,
		borderTopWidth: 0,
		borderColor: "#0A84FF",
		paddingHorizontal: 16,
		paddingBottom: 12,
		paddingTop: 8,
		overflow: "hidden",
		boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
		zIndex: 1000,
		marginTop: 0,
	},
	buildInfoVersion: {
		color: "#fff",
		fontSize: 12,
		marginTop: 4,
	},
	buildInfoDate: {
		color: "#666",
		fontSize: 12,
	},
});

export default AppHomeWithData;
